import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import PlayCard from './PlayCard'
import { ButtonTabs } from '@/components/ui/ButtonTabs'
import { LabelFilterBar } from '@/components/labels/LabelFilterBar'
import { usePlaybookLabels, UNTAGGED_FILTER } from '@/hooks/UsePlaybookLabels'
import { EDITOR_TAB_OPTIONS, type EditorTab } from './Types'
import { useConceptsByCategory } from '@/hooks/UseConceptsByCategory'
import { VOCABULARY_CATEGORY_LABELS, VOCABULARY_CATEGORY_LABELS_PLURAL } from '@/constants/VocabularyLabels'
import type { ConceptCategory } from '@/db/Types'

/** Maps EditorTab values to ConceptCategory values */
export const TAB_TO_CATEGORY: Partial<Record<EditorTab, ConceptCategory>> = {
	routes: 'route',
	blocking: 'blocking',
	motion: 'motion',
	protection: 'protection',
}

/** EditorTab values that represent concept categories — derived from TAB_TO_CATEGORY */
export const CONCEPT_CATEGORY_TABS = Object.keys(TAB_TO_CATEGORY) as EditorTab[]

interface ConceptCategoryTabProps {
	playbookId: string
	activeTab: EditorTab
	onTabChange: (tab: EditorTab) => void
	onDeleteConcept: (id: string) => void
}

export function ConceptCategoryTab({
	playbookId,
	activeTab,
	onTabChange,
	onDeleteConcept,
}: ConceptCategoryTabProps) {
	const navigate = useNavigate()
	const category = TAB_TO_CATEGORY[activeTab]
	if (!category) {
		throw new Error(`ConceptCategoryTab rendered with non-concept tab: ${activeTab}`)
	}

	const singularLabel = VOCABULARY_CATEGORY_LABELS[category] ?? category
	const pluralLabel = VOCABULARY_CATEGORY_LABELS_PLURAL[category] ?? category

	const { concepts, isLoading } = useConceptsByCategory(playbookId, category)

	// Label filtering
	const {
		labels: playbookLabels,
		activeFilter: activeLabelFilter,
		setActiveFilter: setActiveLabelFilter,
		createLabel,
		reorderLabels,
	} = usePlaybookLabels(playbookId)

	// Compute label counts for concepts in this category
	const { counts: labelCounts, untaggedCount } = useMemo(() => {
		const c: Record<string, number> = {}
		let untagged = 0
		for (const concept of concepts) {
			if (!concept.labels || concept.labels.length === 0) {
				untagged++
			} else {
				for (const label of concept.labels) {
					c[label.id] = (c[label.id] || 0) + 1
				}
			}
		}
		return { counts: c, untaggedCount: untagged }
	}, [concepts])

	// Apply label filter
	const filteredConcepts = useMemo(() => {
		if (!activeLabelFilter) return concepts
		if (activeLabelFilter === UNTAGGED_FILTER) {
			return concepts.filter(c => !c.labels || c.labels.length === 0)
		}
		return concepts.filter(c =>
			c.labels?.some(l => l.id === activeLabelFilter),
		)
	}, [concepts, activeLabelFilter])

	const handleNavigateToConceptEditor = useCallback((conceptId?: string) => {
		const path = conceptId
			? `/playbooks/${playbookId}/concept/${conceptId}`
			: `/playbooks/${playbookId}/concept/new?category=${category}`
		navigate(path)
	}, [playbookId, category, navigate])

	return (
		<>
			{/* Toolbar */}
			<div className="playbook-editor-tabs">
				<div className="playbook-editor-tabs-content">
					<ButtonTabs
						options={EDITOR_TAB_OPTIONS}
						value={activeTab}
						onChange={onTabChange}
						aria-label="Content type"
					/>

					<div className="playbook-editor-tabs-divider" />

					<div className="playbook-editor-toolbar-buttons">
						<button
							onClick={() => handleNavigateToConceptEditor()}
							className="playbook-editor-action-button playbook-editor-action-button--primary"
						>
							<Plus className="icon-sm" />
							<span>New {singularLabel}</span>
						</button>
					</div>

					<div className="playbook-editor-tabs-divider" />

					<LabelFilterBar
						labels={playbookLabels}
						counts={labelCounts}
						totalCount={concepts.length}
						untaggedCount={untaggedCount}
						activeFilter={activeLabelFilter}
						onFilterChange={setActiveLabelFilter}
						onCreateLabel={async (data) => { await createLabel(data) }}
						onReorder={reorderLabels}
					/>
				</div>
			</div>

			{/* Content */}
			<div className="playbook-editor-content">
				{isLoading ? (
					<div className="playbook-editor-concepts-loading">
						<p className="playbook-editor-concepts-loading-text">Loading {pluralLabel.toLowerCase()}...</p>
					</div>
				) : filteredConcepts.length === 0 ? (
					<div className="playbook-editor-concepts-empty">
						<p className="playbook-editor-concepts-empty-text">
							{activeLabelFilter
								? `No ${pluralLabel.toLowerCase()} matching your filter`
								: `No ${pluralLabel.toLowerCase()} yet`
							}
						</p>
						{!activeLabelFilter && (
							<button
								onClick={() => handleNavigateToConceptEditor()}
								className="playbook-editor-empty-button"
							>
								Create your first {singularLabel.toLowerCase()}
							</button>
						)}
					</div>
				) : (
					<div className="playbook-editor-sections">
						<div className="playbook-editor-section-header">
							<h2>{pluralLabel}</h2>
							<p className="playbook-editor-section-count">
								{filteredConcepts.length} {filteredConcepts.length === 1 ? singularLabel.toLowerCase() : pluralLabel.toLowerCase()}
							</p>
						</div>
						<div className="playbook-editor-grid">
							{filteredConcepts.map((concept, index) => (
								<PlayCard
									key={`concept-${concept.id}`}
									id={String(concept.id)}
									name={concept.name}
									animateIndex={index}
									cardType="concept"
									category={concept.category}
									thumbnail={concept.thumbnail ?? undefined}
									onOpen={() => handleNavigateToConceptEditor(concept.id)}
									onRename={() => handleNavigateToConceptEditor(concept.id)}
									onDelete={() => onDeleteConcept(concept.id)}
									onDuplicate={() => handleNavigateToConceptEditor(concept.id)}
									labelObjects={concept.labels?.map(l => ({ id: l.id, name: l.name, color: l.color }))}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</>
	)
}
